// A database on the System overview: NextAdmin demo `/saas` metric card (size, growth, connections, cache, load).
import { Link } from 'react-router-dom';
import { buttonStyles } from '../../../components/tailgrids/core/button.js';
import { Badge, Card } from '../../../components/ui/index.js';
import type { SystemDatabaseSummary } from '../../../generated/api/types.gen.js';
import { formatBytes, formatCount, formatRatio } from './metric-format.js';
import { UsageRow } from './UsageRow.js';

const DATABASE_NAMES: Record<SystemDatabaseSummary['id'], string> = {
  app: 'Application database',
  market: 'Market database',
};

export function databaseName(id: SystemDatabaseSummary['id']): string {
  return DATABASE_NAMES[id];
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-sm font-medium text-heading">{value}</dd>
    </div>
  );
}

export function DatabaseCard({ database }: { database: SystemDatabaseSummary }) {
  const connectionsUsed =
    database.connections !== null && database.maxConnections ? database.connections / database.maxConnections : null;
  const growth = database.growthBytesPerDay;
  return (
    <Card
      title={databaseName(database.id)}
      className="xl:col-span-6"
      actions={
        <Link
          to={`/admin/system/databases/${database.id}`}
          className={buttonStyles({ appearance: 'outline', size: 'xs', className: 'px-3 py-1.5' })}
          aria-label={`Open the ${databaseName(database.id).toLowerCase()}`}
        >
          Open
        </Link>
      }
    >
      {database.hostId && <Badge variant="neutral">on {database.hostId}</Badge>}
      <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Fact label="Size" value={formatBytes(database.sizeBytes)} />
        <Fact label="Growth a day" value={growth === null ? '—' : `${growth >= 0 ? '+' : ''}${formatBytes(growth)}`} />
        <Fact
          label="Transactions"
          value={database.transactionsPerSecond === null ? '—' : `${formatCount(database.transactionsPerSecond)}/s`}
        />
        <Fact label="Read from memory" value={formatRatio(database.cacheHit, 1)} />
      </dl>
      <div className="mt-4">
        <UsageRow
          label="Connections"
          ratio={connectionsUsed}
          detail={
            database.connections !== null ? `${database.connections} of ${database.maxConnections ?? '—'}` : undefined
          }
        />
      </div>
    </Card>
  );
}
